export const ACTOR = {
  CORTNEY: { type: "CORTNEY", label: "Cortney" },
  ENGINEERING: { type: "ENGINEERING", label: "Engineering" },
  SYSTEM: { type: "SYSTEM", label: "System" },
  APP: { type: "SYSTEM", label: "App" },
};

const RETENTION_STEPS_UNTIL_COMPLETION = [
  {
    key: "logged",
    title: "FOIA request logged",
    waitingOnLabel: null,
    actionUiType: "none",
    isExternallyVisible: true,
    completion: {
      actor: ACTOR.CORTNEY,
      message: (request) => `Cortney logged FOIA request ${request.trackingNumber}.`,
    },
  },
  {
    key: "matched",
    title: "Patient matched",
    waitingOnLabel: null,
    actionUiType: "none",
    isExternallyVisible: false,
    completion: {
      actor: ACTOR.SYSTEM,
      message: () => "The app matched the request to a patient record.",
    },
  },
  {
    key: "window",
    title: "Appointment window determined",
    waitingOnLabel: null,
    actionUiType: "none",
    isWindowStep: true,
    isExternallyVisible: false,
    completion: {
      actor: ACTOR.SYSTEM,
      message: () => "The app checked each appointment against the 30-day retention window.",
    },
  },
  {
    key: "intake_email",
    title: "Intake email sent to the COR",
    waitingOnLabel: "Cortney",
    actionUiType: "email_draft",
    isExternallyVisible: true,
    completion: {
      actor: ACTOR.CORTNEY,
      message: () => "Cortney sent the intake email to the COR.",
    },
  },
  {
    key: "engineering_retain",
    title: "Engineering retains the recording and sends it to Cortney",
    waitingOnLabel: "Engineering",
    actionUiType: "mark_retained",
    isExternallyVisible: false,
    completion: {
      actor: ACTOR.ENGINEERING,
      message: () => "Engineering confirmed the recording is retained and sent to Cortney.",
    },
  },
  {
    key: "data_received",
    title: "Data received",
    waitingOnLabel: "Cortney",
    actionUiType: "milestone",
    milestoneLabel: "Data received",
    isExternallyVisible: false,
    completion: {
      actor: ACTOR.CORTNEY,
      message: () => "Cortney confirmed she received the data from Engineering.",
    },
  },
  {
    key: "cor_requested",
    title: "COR requested the data",
    waitingOnLabel: "Cortney",
    actionUiType: "milestone",
    milestoneLabel: "COR requested the data",
    isExternallyVisible: true,
    completion: {
      actor: ACTOR.CORTNEY,
      message: () => "Cortney logged that the COR requested the data.",
    },
  },
  {
    key: "uploaded",
    title: "Files uploaded to SharePoint",
    waitingOnLabel: "Cortney",
    actionUiType: "milestone",
    milestoneLabel: "Files uploaded to SharePoint",
    isExternallyVisible: true,
    completion: {
      actor: ACTOR.CORTNEY,
      message: (request) => `Cortney confirmed the files were uploaded to the "${request.sharepointFolderName}" SharePoint folder.`,
    },
  },
  {
    key: "completion_email",
    title: "Completion email sent to the COR",
    waitingOnLabel: "Cortney",
    actionUiType: "email_draft",
    isExternallyVisible: true,
    completion: {
      actor: ACTOR.CORTNEY,
      message: () => "Cortney sent the completion email to the COR.",
    },
  },
];


export const DELETION_STEPS = [
  {
    key: "deletion_requested",
    title: "Deletion requested",
    waitingOnLabel: "Cortney",
    actionUiType: "request_deletion",
    isExternallyVisible: true,
    completion: {
      actor: ACTOR.CORTNEY,
      message: (request) => `Cortney logged a deletion request for ${request.trackingNumber}.`,
    },
  },
  {
    key: "engineering_delete",
    title: "Engineering deletes the data",
    waitingOnLabel: "Engineering",
    actionUiType: "mark_deleted",
    isExternallyVisible: false,
    completion: {
      actor: ACTOR.ENGINEERING,
      message: () => "Engineering confirmed the data has been deleted.",
    },
  },
  {
    key: "deletion_email",
    title: "Deletion confirmation email sent to the COR",
    waitingOnLabel: "Cortney",
    actionUiType: "email_draft",
    isExternallyVisible: true,
    completion: {
      actor: ACTOR.CORTNEY,
      message: () => "Cortney sent the deletion confirmation email to the COR.",
    },
  },
];

export const DELETION_STEP_KEYS = new Set(DELETION_STEPS.map((step) => step.key));

const CLOSED_STEP = {
  key: "closed",
  title: "Request closed",
  waitingOnLabel: null,
  actionUiType: "none",
  isExternallyVisible: true,
  completion: {
    actor: ACTOR.SYSTEM,
    message: () => "Request closed.",
  },
};

// One request is one timeline: retention, then deletion, then closed.
export const RETENTION_STEPS = [...RETENTION_STEPS_UNTIL_COMPLETION, ...DELETION_STEPS, CLOSED_STEP];

export const CANNOT_RETAIN_STEPS = [
  RETENTION_STEPS_UNTIL_COMPLETION[0],
  RETENTION_STEPS_UNTIL_COMPLETION[1],
  RETENTION_STEPS_UNTIL_COMPLETION[2],
  {
    key: "cannot_retain_email",
    title: "Cannot-retain email sent to the COR",
    waitingOnLabel: "Cortney",
    actionUiType: "email_draft",
    isExternallyVisible: true,
    completion: {
      actor: ACTOR.CORTNEY,
      message: () => "Cortney sent the cannot-retain email to the COR.",
    },
  },
  {
    key: "closed_not_retained",
    title: "Request closed (not retained)",
    waitingOnLabel: null,
    actionUiType: "none",
    isExternallyVisible: true,
    completion: {
      actor: ACTOR.SYSTEM,
      message: () => "Request closed — the recording could not be retained.",
    },
  },
];

export function getStepsForBranch(branch) {
  return branch === "cannotRetain" ? CANNOT_RETAIN_STEPS : RETENTION_STEPS;
}

export function getStepIndex(branch, stepKey) {
  return getStepsForBranch(branch).findIndex((step) => step.key === stepKey);
}

export function getStepDefinition(branch, stepKey) {
  return getStepsForBranch(branch).find((step) => step.key === stepKey) || null;
}

export function isTerminalStep(branch, stepKey) {
  const steps = getStepsForBranch(branch);
  return steps[steps.length - 1].key === stepKey;
}

export const STATUS_CHIP_BY_STEP = {
  logged: { label: "Waiting on Customer Delivery", variant: "info" },
  matched: { label: "Waiting on Customer Delivery", variant: "info" },
  window: { label: "Waiting on Customer Delivery", variant: "info" },
  intake_email: { label: "Waiting on Customer Delivery", variant: "info" },
  engineering_retain: { label: "Waiting on Engineering", variant: "warning" },
  data_received: { label: "Waiting on Cortney", variant: "warning" },
  cor_requested: { label: "Data received", variant: "success" },
  uploaded: { label: "Retained", variant: "success" },
  completion_email: { label: "Retained", variant: "success" },
  closed: { label: "Closed", variant: "default" },
  cannot_retain_email: { label: "Waiting on Customer Delivery", variant: "info" },
  closed_not_retained: { label: "Closed (not retained)", variant: "default" },
  deletion_requested: { label: "Retained (no deletion requested)", variant: "success" },
  engineering_delete: { label: "Waiting on Engineering", variant: "warning" },
  deletion_email: { label: "Deleted (COR email pending)", variant: "success" },
};

export const NEXT_ACTION_BY_STEP = {
  logged: "Next: the app matches this request to a patient.",
  matched: "Next: the app checks each appointment against the 30-day window.",
  window: "Next: the app checks each appointment against the 30-day window.",
  intake_email: "Next: Cortney sends the intake email to the COR.",
  cannot_retain_email: "Next: Cortney sends the cannot-retain email to the COR.",
  engineering_retain: "Next: Engineering marks the recording retained and sent to Cortney.",
  data_received: "Next: Cortney confirms she received the data from Engineering.",
  cor_requested: "Next: Cortney logs that the COR requested the data.",
  uploaded: "Next: Cortney confirms the files are uploaded to SharePoint.",
  completion_email: "Next: Cortney sends the completion email to the COR.",
  closed: "This request is closed.",
  closed_not_retained: "This request is closed — the recording could not be retained.",
  deletion_requested: "Next: Cortney logs the COR's deletion request when it arrives.",
  engineering_delete: "Next: Engineering deletes the data and marks it deleted.",
  deletion_email: "Next: Cortney sends the deletion confirmation email to the COR.",
};
